import { RegionId } from "@sp-crm/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { useActivityTypeOptions } from "components/tasks/activity-type-options";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { useNow, useRegionId } from "store/selectors/hooks";
import { groupActivityByDate, MyActivityItem, trailingMonthRange } from "./my-activity";
import { MyActivityRow, toMyActivityItem } from "./my-activity-row";

const dayLabel = (date: string): string =>
    new Date(`${date}T00:00:00.000Z`).toLocaleDateString("en-US", {
        weekday: "long",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
    });

export const MyActivityTab: React.FC<unknown> = () => {
    const now = useNow();
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const input = {
        ...trailingMonthRange(now),
        regionId: regionId ?? ("" as RegionId),
    };
    const activityTypes = useActivityTypeOptions();

    const activityQuery = useQuery({
        ...trpcClient.touchpoint.myActivity.queryOptions(input),
        enabled: !!regionId,
    });

    const onActivityTypeSaved = () =>
        queryClient.invalidateQueries({
            queryKey: trpcClient.touchpoint.myActivity.queryKey(input),
        });

    const items: MyActivityItem[] = (activityQuery.data ?? []).map(toMyActivityItem);
    const days = groupActivityByDate(items);

    if (activityQuery.isLoading) {
        return (
            <div className="flex justify-center rounded-lg bg-white p-12 ring-1 ring-gray-300">
                <Spinner />
            </div>
        );
    }

    if (days.length === 0) {
        return (
            <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-500 ring-1 ring-gray-300">
                No activity recorded in the last 30 days. Emails, calls, texts, visits,
                and completed activities show up here.
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <p className="text-sm text-gray-500">
                Everything you logged in the last 30 days.
            </p>
            {days.map(day => (
                <section
                    key={day.date}
                    className="rounded-lg bg-white ring-1 ring-gray-300">
                    <h3 className="border-b border-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {dayLabel(day.date)}
                    </h3>
                    <div className="divide-y divide-gray-100">
                        {day.items.map(item => (
                            <MyActivityRow
                                key={item.id}
                                item={item}
                                activityTypes={activityTypes}
                                onSaved={onActivityTypeSaved}
                            />
                        ))}
                    </div>
                </section>
            ))}
        </div>
    );
};
